'use client'

import dynamic from 'next/dynamic'
import type { ButtonProps } from '@shc/ui'
import slugify from '@sindresorhus/slugify'
import { CTABackgroundBlock } from '@shc/ui'
import { useContext, useState } from 'react'
import { modalImportMap } from '@lib/constants'
import { richTextRender } from '@lib/rich-text-base'
import type { Dispatch, SetStateAction } from 'react'
import { mapButtonVariant } from '@lib/mappers/sharp-ui-mapper'
import useAnalytics, { type SPContext } from '@hooks/use-analytics'
import type { TypeCtaBackgroundBlockFields } from '@lib/generated-types'
import { linkAttributes, paddingMap, containerWidthMap, imageMap } from '@lib/token-maps'
import {
  BlockContext,
  ItemContext,
  PageContext,
  PlaceContext,
  ProviderContext,
} from '@lib/analytics'

type TypeGetButtonProps = {
  label: TypeCtaBackgroundBlockFields['button1Label'] | TypeCtaBackgroundBlockFields['button2Label']
  modal: TypeCtaBackgroundBlockFields['button1Modal'] | TypeCtaBackgroundBlockFields['button2Modal']
  link: TypeCtaBackgroundBlockFields['button1Link'] | TypeCtaBackgroundBlockFields['button2Link']
  anchorOrExternalLink:
    | TypeCtaBackgroundBlockFields['button1AnchorOrExternalLink']
    | TypeCtaBackgroundBlockFields['button2AnchorOrExternalLink']
  style: TypeCtaBackgroundBlockFields['button1Style'] | TypeCtaBackgroundBlockFields['button2Style']
  setModalOpen: Dispatch<SetStateAction<boolean>>
}

const CtaBackgroundBlock = ({
  sectionLink,
  heading,
  description,
  button1Label,
  button1AnchorOrExternalLink,
  button1Link,
  button1Modal,
  button1Style,
  button2Label,
  button2AnchorOrExternalLink,
  button2Link,
  button2Modal,
  button2Style,
  width,
  backgroundColor,
  backgroundImage,
  paddingTop,
  paddingBottom,
}: TypeCtaBackgroundBlockFields) => {
  const { trackSnowplow } = useAnalytics()
  const [isButton1ModalOpen, setIsButton1ModalOpen] = useState(false)
  const [isButton2ModalOpen, setIsButton2ModalOpen] = useState(false)

  // Environment contexts
  const pageContext = useContext(PageContext)
  const placeContext = useContext(PlaceContext)
  const providerContext = useContext(ProviderContext)
  const blockContext = useContext(BlockContext)
  const itemContext = useContext(ItemContext)
  const environmentContexts: SPContext[] = [
    pageContext,
    placeContext,
    providerContext,
    blockContext,
    itemContext,
  ].filter((context) => context !== null)

  let Button1Modal =
    button1Modal &&
    dynamic(() => modalImportMap[button1Modal], {
      ssr: false,
    })
  let Button2Modal =
    button2Modal &&
    dynamic(() => modalImportMap[button2Modal], {
      ssr: false,
    })

  const getButtonProperties = ({
    label,
    modal,
    link,
    anchorOrExternalLink,
    style,
    setModalOpen,
  }: TypeGetButtonProps): ButtonProps => {
    const getLinkAttributes = !modal ? linkAttributes(link, anchorOrExternalLink) : undefined

    const onClick = () => {
      const componentContext: SPContext = {
        name: 'component',
        data: { component_text: label ?? '' },
      }

      if (!modal) {
        trackSnowplow({
          event: {
            name: 'navigation_click',
            data: {
              navigation_subject: label ?? '',
              navigation_tree: label ?? '',
              navigation_level: 1,
              navigation_url: getLinkAttributes?.href,
            },
          },
          contexts: [...environmentContexts, componentContext],
        })
      } else {
        setModalOpen(true)

        trackSnowplow({
          event: { name: 'component_click', data: {} },
          contexts: [...environmentContexts, componentContext],
        })

        trackSnowplow({
          event: { name: 'modal_open', data: {} },
          contexts: [
            ...environmentContexts,
            componentContext,
            { name: 'modal', data: { modal_name: modal ?? '' } },
          ],
        })
      }
    }

    return { label, onClick, ...mapButtonVariant(style), ...getLinkAttributes }
  }

  return (
    <>
      <CTABackgroundBlock
        id={sectionLink && slugify(sectionLink)}
        heading={heading}
        description={richTextRender(description)}
        paddingBottom={paddingMap[paddingBottom]}
        paddingTop={paddingMap[paddingTop]}
        backgroundColor={backgroundColor}
        containerSize={containerWidthMap[width]}
        {...(backgroundImage && {
          image: {
            src: imageMap[backgroundImage],
            alt: backgroundImage,
          },
        })}
        buttonPrimary={getButtonProperties({
          label: button1Label,
          modal: button1Modal,
          anchorOrExternalLink: button1AnchorOrExternalLink,
          link: button1Link,
          style: button1Style,
          setModalOpen: setIsButton1ModalOpen,
        })}
        {...(button2Label && {
          buttonSecondary: getButtonProperties({
            label: button2Label,
            modal: button2Modal,
            anchorOrExternalLink: button2AnchorOrExternalLink,
            link: button2Link,
            style: button2Style,
            setModalOpen: setIsButton2ModalOpen,
          }),
        })}
      />
      {Button1Modal && (
        <Button1Modal isModalOpen={isButton1ModalOpen} setIsModalOpen={setIsButton1ModalOpen} />
      )}
      {Button2Modal && (
        <Button2Modal isModalOpen={isButton2ModalOpen} setIsModalOpen={setIsButton2ModalOpen} />
      )}
    </>
  )
}

export default CtaBackgroundBlock
